import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Control, FieldErrors, useFieldArray, UseFormRegister, useWatch } from "react-hook-form";
import { Section } from "../../components/section";
import { ProgramInputs } from "../../schemas/inputs";

interface EvaluationStageCriteriaProps {
    stageIndex: number;
    control: Control<ProgramInputs>;
    register: UseFormRegister<ProgramInputs>;
    errors: FieldErrors<ProgramInputs>;
}

export const EvaluationStageCriteria = ({ stageIndex, control, register, errors }: EvaluationStageCriteriaProps) => {
    const { fields, append, remove, update, insert } = useFieldArray({
        control,
        name: `evaluations.${stageIndex}.sections.0.criteria`
    });

    const [showDescription, setShowDescription] = useState<Record<number, boolean>>({});

    const toggleDescription = (index: number, show: boolean) => {
        setShowDescription(prev => ({ ...prev, [index]: show }));
    };

    const criteria = useWatch({
        control,
        name: `evaluations.${stageIndex}.sections.0.criteria`,
    });

    useEffect(() => {
        if (criteria && Array.isArray(criteria)) {
            const initialShowDescription: Record<number, boolean> = {};
            criteria.forEach((criterion: any, index: number) => {
                if (criterion?.description && criterion.description.trim() !== "") {
                    initialShowDescription[index] = true;
                }
            });
            if (Object.keys(initialShowDescription).length > 0) {
                setShowDescription(prev => ({ ...prev, ...initialShowDescription }));
            }
        }
    }, [criteria]);

    const addCriterion = () => {
        append({
            name: '',
            description: ''
        });
    };

    const cloneCriterion = (criterion: any) => {
        const { id, createdAt, updatedAt, order, isDeleted, ...copy } = criterion ?? {};
        return {
            ...copy,
            name: copy.name ? `${copy.name} (cópia)` : '',
            isDeleted: false
        };
    };

    const deleteCriterion = (index: number) => {
        const currentCriterion = (criteria?.[index] ?? fields[index]) as any;
        if (currentCriterion?.id) {
            update(index, { ...currentCriterion, isDeleted: true } as any);
            return;
        }

        remove(index);
    };

    const duplicateCriterion = (index: number) => {
        const currentCriterion = criteria?.[index] ?? fields[index];
        insert(index + 1, cloneCriterion(currentCriterion));
    };

    return (
        <div style={{width: '100%', margin: '0px', padding: '0px' }}>
            {fields.map((field, index) => {
                const currentCriterion = (criteria?.[index] ?? field) as any;
                if (currentCriterion?.isDeleted) return null;

                return (
                <Section key={field.id} isOpen={true} label={`Critério ${index + 1}`} hasBorder={true} hasMarginTop={true} showActions={true} onDelete={() => deleteCriterion(index)} onDuplicate={() => duplicateCriterion(index)}>
                    <Row>
                        <Col>
                            <Form.Group>
                                <Form.Label required>Nome do critério</Form.Label>
                                <Form.Control
                                    placeholder="Digite o nome do critério"
                                    {...register(`evaluations.${stageIndex}.sections.0.criteria.${index}.name`)}
                                    isInvalid={!!errors.evaluations?.[stageIndex]?.sections?.[0]?.criteria?.[index]?.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.evaluations?.[stageIndex]?.sections?.[0]?.criteria?.[index]?.name?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className={!showDescription[index] ? "" : "d-none"}>
                        <Col>
                            <Button variant="link" onClick={() => toggleDescription(index, true)} className="d-flex align-items-center gap-2 text-decoration-none">
                                <FontAwesomeIcon icon={['fas', 'plus']} />
                                Adicionar Descrição
                            </Button>
                        </Col>
                    </Row>
                    <Row className={showDescription[index] ? "" : "d-none"}>
                        <Col>
                            <Form.Group>
                                <Form.Label>Descrição</Form.Label>
                                <div style={{ display: 'flex' }}>
                                    <Form.Control
                                        placeholder="Digite a descrição do critério"
                                        {...register(`evaluations.${stageIndex}.sections.0.criteria.${index}.description`)}
                                        isInvalid={!!errors.evaluations?.[stageIndex]?.sections?.[0]?.criteria?.[index]?.description}
                                    />
                                    <Button variant="link" onClick={() => toggleDescription(index, false)} className="d-flex align-items-center gap-2 text-decoration-none">
                                        <FontAwesomeIcon icon={['fas', 'close']} />
                                    </Button>
                                </div>
                                <Form.Control.Feedback type="invalid">
                                    {errors.evaluations?.[stageIndex]?.sections?.[0]?.criteria?.[index]?.description?.message}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                </Section>
                );
            })}
            <Row className="mt-3">
                <Col>
                    <Button type="button" onClick={addCriterion} className="d-flex align-items-center gap-2 bg-transparent text-primary border border-dashed border-primary">
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                        Adicionar critério
                    </Button>
                </Col>
            </Row>
        </div>
    );
};
